// src/pages/time/TrackerPage.jsx
// Extracted from App.js - Tracker list view
// BRIO Migration: Using code "1b" (table compact)
//
// ============================================================================
// PARTIAL INVOICING & CLOSURE EXTENSION
// ============================================================================
// Added: Expandable stage rows with Closed% and Invoiced% pills.
// Expanded view shows ledger entries + inline add forms.
// Data source: project_stage_status (rolled-up) + project_stage_entries (ledger).
// Analytics reads ONLY from project_stage_status — never from entries.
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Download, Upload, Clock, Loader, ChevronDown, ChevronRight, X } from 'lucide-react';
import {
  BrioAnalyticsSurface,
  BrioFrame,
  BrioByCode,
  BrioTable,
  BrioColorIndicator,
  BrioIconButton,
  BrioActionGroup,
  BrioDatePicker,
  BrioDateRangePicker,
  getTablePropsFromCode
} from '../../components/BrioComponents';
// Presentation code for this page
const PRESENTATION_CODE = '1b';

// ============================================================================
// SUB-COMPONENT: StageStatusSection
// ============================================================================
// Shows per-project stage status with expandable ledger rows.
// Only visible to admin/supervisor when a project filter is active.
// ============================================================================

function StageStatusSection({
  projectId,
  activeTasks,
  currentUser,
  stageStatusHook,
  stageEntriesHook,
  useSupabaseDB,
}) {
  const [expandedStageId, setExpandedStageId] = useState(null);
  const [addForm, setAddForm] = useState({ type: '', percent: '', date: '', note: '' });
  const [addError, setAddError] = useState('');
  const [saving, setSaving] = useState(false);

  const isAdminOrSupervisor = currentUser.role === 'admin' || currentUser.role === 'admin-pa' || currentUser.role === 'supervisor';

  // Load stage statuses when project changes
  useEffect(() => {
    if (projectId && useSupabaseDB) {
      stageStatusHook.fetchProjectStageStatus(projectId);
    }
  }, [projectId, useSupabaseDB]); // eslint-disable-line react-hooks/exhaustive-deps

  // Collapse when project changes
  useEffect(() => {
    setExpandedStageId(null);
    stageEntriesHook.clearEntries();
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Toggle expand/collapse for a stage
  const handleToggleStage = useCallback(async (stageId) => {
    if (expandedStageId === stageId) {
      // Collapse
      setExpandedStageId(null);
      stageEntriesHook.clearEntries();
      setAddForm({ type: '', percent: '', date: '', note: '' });
      setAddError('');
    } else {
      // Expand — lazy-load entries
      setExpandedStageId(stageId);
      setAddForm({ type: '', percent: '', date: '', note: '' });
      setAddError('');
      await stageEntriesHook.fetchEntries(projectId, stageId);
    }
  }, [expandedStageId, projectId, stageEntriesHook]);

  // Add entry handler
  const handleAddEntry = async (stageId, type) => {
    const percent = parseInt(addForm.percent, 10);
    if (!percent || percent < 1 || percent > 100) {
      setAddError('Enter a valid percent (1–100).');
      return;
    }
    if (!addForm.date) {
      setAddError('Date is required.');
      return;
    }

    const remaining = stageEntriesHook.getRemainingPercent(type);
    if (percent > remaining) {
      setAddError(`Only ${remaining}% remaining for ${type}.`);
      return;
    }

    setSaving(true);
    setAddError('');

    const result = await stageEntriesHook.addEntry({
      projectId,
      stageId,
      type,
      percent,
      entryDate: addForm.date,
      note: addForm.note,
    });

    setSaving(false);

    if (result.success) {
      setAddForm({ type: '', percent: '', date: '', note: '' });
    } else {
      setAddError(result.error || 'Failed to add entry.');
    }
  };

  // Delete entry handler
  const handleDeleteEntry = async (entryId, stageId) => {
    const result = await stageEntriesHook.deleteEntry(entryId, projectId, stageId);
    if (!result.success) {
      alert('Failed to delete entry: ' + (result.error || 'Unknown error'));
    }
  };

  if (!projectId || !useSupabaseDB) return null;
  if (!isAdminOrSupervisor) return null;

  return (
    <BrioAnalyticsSurface padding="m">
      <div style={{ marginBottom: '8px' }}>
        <span className="brio-font-medium" style={{ fontSize: '13px' }}>Stage Progress</span>
        <span className="brio-text-xs brio-text-grey" style={{ marginLeft: '8px' }}>
          Closure & invoicing status per stage
        </span>
      </div>

      <div style={{ borderTop: '1px solid var(--brio-border)' }}>
        {activeTasks.map(stage => {
          const status = stageStatusHook.getStatusFromCache(projectId, stage.id);
          const isExpanded = expandedStageId === stage.id;
          const invoiceEntries = stageEntriesHook.entries.filter(e => e.type === 'invoice');
          const closureEntries = stageEntriesHook.entries.filter(e => e.type === 'closure');

          return (
            <div key={stage.id} style={{ borderBottom: '1px solid var(--brio-border)' }}>
              {/* ── Collapsed row ── */}
              <div
                onClick={() => handleToggleStage(stage.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 4px',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
                className="brio-row--hoverable"
              >
                {/* Caret */}
                {isExpanded
                  ? <ChevronDown className="w-4 h-4" style={{ color: 'var(--brio-grey)', flexShrink: 0 }} />
                  : <ChevronRight className="w-4 h-4" style={{ color: 'var(--brio-grey)', flexShrink: 0 }} />
                }

                {/* Stage name */}
                <span className="brio-font-medium" style={{ flex: 1, fontSize: '12px' }}>
                  {stage.name}
                </span>

                {/* Closed % pill */}
                <PercentPill
                    label="Closed"
                    value={status.closedPercent}
                    color={status.closedPercent >= 100 ? 'var(--brio-green, #16a34a)' : 'var(--brio-grey)'}
                  />

                {/* Invoiced % pill */}
                <PercentPill
                  label="Invoiced"
                  value={status.invoicedPercent}
                  color={status.invoicedPercent >= 100 ? 'var(--brio-gold)' : 'var(--brio-grey)'}
                />
              </div>

              {/* ── Expanded detail ── */}
              {isExpanded && (
                <div style={{
                  padding: '4px 0 12px 28px',
                  background: 'var(--brio-grey-light, #f9f9f9)',
                }}>
                  {stageEntriesHook.loadingEntries ? (
                    <div className="brio-text-xs brio-text-grey" style={{ padding: '12px 0' }}>
                      <Loader className="w-3 h-3 inline-block" style={{ animation: 'spin 1s linear infinite', marginRight: '6px' }} />
                      Loading entries…
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                      {/* ── Invoicing ledger ── */}
                      <LedgerSection
                        title="Invoicing"
                        type="invoice"
                        entries={invoiceEntries}
                        remaining={stageEntriesHook.getRemainingPercent('invoice')}
                        stageId={stage.id}
                        addForm={addForm}
                        setAddForm={setAddForm}
                        addError={addError}
                        saving={saving}
                        onAdd={handleAddEntry}
                        onDelete={handleDeleteEntry}
                        currentUser={currentUser}
                      />

                      {/* ── Closure ledger ── */}
                      <LedgerSection
                        title="Closure"
                        type="closure"
                        entries={closureEntries}
                        remaining={stageEntriesHook.getRemainingPercent('closure')}
                        stageId={stage.id}
                        addForm={addForm}
                        setAddForm={setAddForm}
                        addError={addError}
                        saving={saving}
                        onAdd={handleAddEntry}
                        onDelete={handleDeleteEntry}
                        currentUser={currentUser}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </BrioAnalyticsSurface>
  );
}


// ============================================================================
// SUB-COMPONENT: PercentPill
// Small inline badge showing "Label XX%"
// ============================================================================
function PercentPill({ label, value, color }) {
  return (
    <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          fontSize: '10px',
          fontWeight: 400,
          padding: '2px 8px',
          border: `1px solid ${value > 0 ? color : 'var(--brio-border)'}`,
          color: value > 0 ? color : 'var(--brio-grey)',
          background: value >= 100 ? `${color}11` : 'transparent',
          whiteSpace: 'nowrap',
        }}
      >
        {label} {value}%
      </span>
  );
}


// ============================================================================
// SUB-COMPONENT: LedgerSection
// Shows a list of entries + inline add row for one type (invoice or closure).
// ============================================================================
function LedgerSection({
  title,
  type,
  entries,
  remaining,
  stageId,
  addForm,
  setAddForm,
  addError,
  saving,
  onAdd,
  onDelete,
  currentUser,
}) {
  const isAdminOrSupervisor = currentUser.role === 'admin' || currentUser.role === 'admin-pa' || currentUser.role === 'supervisor';
  const showAddRow = addForm.type === type;
  const totalPercent = entries.reduce((s, e) => s + e.percent, 0);

  return (
    <div style={{ flex: '1 1 280px', minWidth: '260px' }}>
      {/* Section header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '6px',
        paddingRight: '4px',
      }}>
        <span className="brio-font-medium" style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {title}
        </span>
        <span className="brio-text-xs brio-text-grey">
          {totalPercent}% used · {remaining}% remaining
        </span>
      </div>

      {/* Entries list */}
      {entries.length > 0 ? (
        <div style={{ marginBottom: '6px' }}>
          {entries.map(entry => (
            <div
              key={entry.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '4px 4px',
                fontSize: '11px',
                borderBottom: '1px solid var(--brio-border)',
              }}
            >
              <span className="brio-text-grey" style={{ width: '70px', flexShrink: 0 }}>{entry.entryDate}</span>
              <span className="brio-font-medium" style={{ width: '40px', textAlign: 'right', flexShrink: 0 }}>{entry.percent}%</span>
              <span className="brio-text-grey brio-italic" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {entry.note || '—'}
              </span>
              {isAdminOrSupervisor && (
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(entry.id, stageId); }}
                  title="Delete entry"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '2px',
                    color: 'var(--brio-grey)',
                    flexShrink: 0,
                  }}
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="brio-text-xs brio-text-grey brio-italic" style={{ padding: '6px 4px' }}>
          No {type} entries yet
        </div>
      )}

      {/* Add button / inline form */}
      {isAdminOrSupervisor && remaining > 0 && (
        <>
          {!showAddRow ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setAddForm({
                  type,
                  percent: '',
                  date: new Date().toISOString().split('T')[0],
                  note: '',
                });
              }}
              style={{
                background: 'none',
                border: '1px dashed var(--brio-border)',
                padding: '4px 10px',
                fontSize: '11px',
                cursor: 'pointer',
                color: 'var(--brio-gold)',
                width: '100%',
                textAlign: 'left',
              }}
            >
              + Add {type} entry
            </button>
          ) : (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                border: '1px solid var(--brio-gold)',
                padding: '8px',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                background: '#fff',
              }}
            >
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <BrioDatePicker
                    value={addForm.date}
                    onChange={v => setAddForm(f => ({ ...f, date: v }))}
                    clearable={false}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <input
                      type="number"
                      min="1"
                      max={remaining}
                      placeholder="%"
                      value={addForm.percent}
                      onChange={(e) => setAddForm(f => ({ ...f, percent: e.target.value }))}
                      className="brio-btn brio-btn-secondary"
                      style={{ padding: '4px 8px', fontSize: '11px', width: '60px', textAlign: 'right' }}
                    />
                    <span className="brio-text-xs brio-text-grey">%</span>
                  </div>
                  <input
                    type="text"
                    placeholder="Note (optional)"
                    value={addForm.note}
                    onChange={(e) => setAddForm(f => ({ ...f, note: e.target.value }))}
                    className="brio-btn brio-btn-secondary"
                    style={{ padding: '4px 8px', fontSize: '11px', flex: '1 1 100px', minWidth: '80px' }}
                  />
                </div>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <button
                  onClick={() => onAdd(stageId, type)}
                  disabled={saving}
                  className="brio-btn brio-btn-primary"
                  style={{ padding: '4px 12px', fontSize: '11px' }}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                  onClick={() => setAddForm({ type: '', percent: '', date: '', note: '' })}
                  className="brio-btn brio-btn-secondary"
                  style={{ padding: '4px 12px', fontSize: '11px' }}
                >
                  Cancel
                </button>
                {addError && addForm.type === type && (
                    <span style={{ color: '#dc2626', fontSize: '11px' }}>{addError}</span>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Fully complete indicator */}
      {remaining === 0 && (
          <div className="brio-text-xs" style={{ color: 'var(--brio-green, #16a34a)', padding: '4px', fontStyle: 'italic' }}>
            ✓ 100% {type === 'invoice' ? 'invoiced' : 'closed'}
          </div>
      )}
    </div>
  );
}


// ============================================================================
// MAIN COMPONENT: TrackerPage
// ============================================================================
export default function TrackerPage({
  // Data
  currentUser,
  activeUsers,
  activeClients,
  activeProjects,
  activeTasks,
  timeEntries,
  selected,
  filters,
  currentPage,
  entriesPerPage,
  // Supabase
  useSupabaseDB,
  supabaseTimeEntries,
  // Stage status hooks (new — passed from parent)
  stageStatusHook,
  stageEntriesHook,
  // State setters
  setModal,
  setFilters,
  setSelected,
  setTimeEntries,
  setCurrentPage,
  setEditing,
  // Functions
  filterEntries,
  getProjectsForDropdown,
  exportCSV,
  importCSV,
  calcRev,
  fmtTime,
  fmtCurr,
  fmtProject,
}) {
  const tableProps = getTablePropsFromCode(PRESENTATION_CODE);


  // Get keyset pagination state from Supabase hook (if available)
  const hasMore = useSupabaseDB ? (supabaseTimeEntries.hasMore ?? false) : false;
  const loadingMore = useSupabaseDB ? (supabaseTimeEntries.loadingMore ?? false) : false;

  // Handler for loading more entries via keyset pagination
  const handleLoadMore = async () => {
    if (!useSupabaseDB || !supabaseTimeEntries.loadMoreTimeEntries || loadingMore) return;
    
    // Get the last loaded entry's ID (smallest ID since we order by id DESC)
    const lastEntry = timeEntries[timeEntries.length - 1];
    if (!lastEntry) return;
    
    await supabaseTimeEntries.loadMoreTimeEntries(lastEntry.id, 500);
  };

  // Determine if a specific project is selected in filters
  const selectedProjectId = filters.projectId !== 'all' ? parseInt(filters.projectId, 10) : null;
  const hasStageHooks = stageStatusHook && stageEntriesHook;

  return (
    <div className="brio-flex brio-flex-col brio-analytics-font" style={{ background: '#fff' }}>
      {/* PAGE HEADER BAND: Full-width white background, centered content */}
      <div className="brio-pageband brio-pageband--header">
        <BrioFrame>
          <div className="brio-page-header__content">
            <div className="brio-page-header__left">

            </div>
            <div className="brio-page-header__right">
              <button 
                onClick={() => setModal('addTime')} 
                className="brio-btn brio-btn-primary brio-flex brio-items-center brio-gap-xs"
              >
                <Plus className="w-5 h-5"/>Add Time
              </button>
            </div>
          </div>
        </BrioFrame>
      </div>

      {/* FILTER BAR BAND */}
      <div className="brio-pageband">
        <BrioFrame>
          <div style={{ padding: 'var(--brio-space-s) 0' }}>
            <div className="brio-flex brio-gap-xs brio-flex-wrap brio-mb-s">
              <select 
                value={filters.clientId} 
                onChange={(e) => {setFilters({...filters, clientId: e.target.value}); setCurrentPage(1);}} 
                className="brio-btn brio-btn-secondary"
                style={{ minWidth: '120px' }}
              >
                <option value="all">All Clients</option>
                {activeClients.map(c => <option key={c.id} value={c.id}>{c.organisation || c.name}</option>)}
              </select>
              <select
                value={filters.projectId} 
                onChange={(e) => {setFilters({...filters, projectId: e.target.value}); setCurrentPage(1);}} 
                className="brio-btn brio-btn-secondary"
                style={{ minWidth: '120px' }}
              >
                <option value="all">All Projects</option>
                {getProjectsForDropdown().map(p => <option key={p.id} value={p.id}>{fmtProject(p)}</option>)}
              </select>
              <select 
                value={filters.billable} 
                onChange={(e) => {setFilters({...filters, billable: e.target.value}); setCurrentPage(1);}} 
                className="brio-btn brio-btn-secondary"
                style={{ minWidth: '100px' }}
              >
                <option value="all">All Types</option>
                <option value="true">Billable</option>
                <option value="false">Non-Billable</option>
              </select>
            </div>

              <div className="brio-flex brio-gap-xs brio-items-center">
                <span className="brio-text-m brio-font-medium brio-text-grey">Date Range:</span>
                <BrioDateRangePicker
                  startDate={filters.startDate}
                  endDate={filters.endDate}
                  onChangeStart={v => {setFilters(f => ({...f, startDate: v})); setCurrentPage(1);}}
                  onChangeEnd={v => {setFilters(f => ({...f, endDate: v})); setCurrentPage(1);}}
                  clearable={false}
                />
              {(filters.startDate || filters.endDate) && (
                <button
                  onClick={() => {setFilters({...filters, startDate: '', endDate: ''}); setCurrentPage(1);}}
                  className="brio-btn brio-btn-secondary"
                >
                  Clear
                </button>
              )}
              </div>
          </div>
        </BrioFrame>
      </div>

      {/* CONTENT AREA */}
      <BrioFrame>
        <div className="brio-flex brio-flex-col brio-gap-s" style={{ paddingTop: 'var(--brio-space-s)' }}>

          {/* ============================================================ */}
          {/* STAGE STATUS SECTION (partial invoicing / closure)           */}
          {/* Only shown when a single project is filtered AND Supabase on */}
          {/* ============================================================ */}
          {hasStageHooks && selectedProjectId && !isNaN(selectedProjectId) && (
            <StageStatusSection
              projectId={selectedProjectId}
              activeTasks={activeTasks}
              currentUser={currentUser}
              stageStatusHook={stageStatusHook}
              stageEntriesHook={stageEntriesHook}
              useSupabaseDB={useSupabaseDB}
            />
          )}

          {/* Selection Actions */}
          {selected.length > 0 && (
            <div style={{ background: 'var(--brio-gold-light)', border: '1px solid var(--brio-gold)', padding: '12px 16px' }}>
              <BrioActionGroup>
                <span className="brio-font-medium">{selected.length} selected</span>
                <button onClick={async () => {
                  if (useSupabaseDB) {
                    const result = await supabaseTimeEntries.bulkUpdateTimeEntries(selected, {billable: true});
                    if (!result.success) {
                      alert('Error updating entries: ' + result.error);
                    } else {
                      await supabaseTimeEntries.refetch();
                    }
                  } else {
                    setTimeEntries(timeEntries.map(e => selected.includes(e.id)?{...e,billable:true}:e));
                  }
                  setSelected([]);
                }} className="brio-btn brio-btn-primary" style={{ background: 'var(--brio-green)' }}>Billable</button>
                <button onClick={async () => {
                  if (useSupabaseDB) {
                    const result = await supabaseTimeEntries.bulkUpdateTimeEntries(selected, {billable: false});
                    if (!result.success) {
                      alert('Error updating entries: ' + result.error);
                    } else {
                      await supabaseTimeEntries.refetch();
                    }
                  } else {
                    setTimeEntries(timeEntries.map(e => selected.includes(e.id)?{...e,billable:false}:e));
                  }
                  setSelected([]);
                }} className="brio-btn brio-btn-secondary">Non-Billable</button>
                {(currentUser.role === 'admin' || currentUser.role === 'admin-pa' || currentUser.role === 'supervisor') && (
                  <button onClick={async () => {
                    if (useSupabaseDB) {
                      const result = await supabaseTimeEntries.bulkUpdateTimeEntries(selected, {approved: true});
                      if (!result.success) {
                        alert('Error updating entries: ' + result.error);
                      } else {
                        await supabaseTimeEntries.refetch();
                      }
                    } else {
                      setTimeEntries(timeEntries.map(e => selected.includes(e.id)?{...e,approved:true}:e));
                    }
                    setSelected([]);
                  }} className="brio-btn brio-btn-primary">Approve</button>
                )}
                <button onClick={() => {setModal('confirmDelete');}} className="brio-btn brio-btn-danger">Delete</button>
              </BrioActionGroup>
            </div>
          )}

          {/* Tools Bar */}
          <div className="brio-flex brio-gap-xs brio-items-center brio-flex-wrap">
            <label className="brio-btn brio-btn-secondary brio-flex brio-items-center brio-gap-xs" style={{ cursor: 'pointer' }}>
              <input 
                type="checkbox" 
                checked={filterEntries().length > 0 && selected.length === filterEntries().length}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelected(filterEntries().map(entry => entry.id));
                  } else {
                    setSelected([]);
                  }
                }}
                style={{ width: '16px', height: '16px' }}
              />
              {selected.length === filterEntries().length && filterEntries().length > 0 ? 'Deselect All' : 'Select All'}
            </label>
            <button onClick={exportCSV} className="brio-btn brio-btn-secondary brio-flex brio-items-center brio-gap-xs">
              <Download className="w-4 h-4"/>Export
            </button>
            <label className="brio-btn brio-btn-secondary brio-flex brio-items-center brio-gap-xs" style={{ cursor: 'pointer' }}>
              <Upload className="w-4 h-4"/>Import
              <input type="file" accept=".csv" onChange={importCSV} className="hidden" style={{ display: 'none' }}/>
            </label>

          </div>

          {/* Time Entries Table */}
          <BrioAnalyticsSurface padding="m">
            {(() => {
              const allFilteredEntries = filterEntries();
              const totalEntries = allFilteredEntries.length;
              const totalPages = Math.ceil(totalEntries / entriesPerPage);
              const startIndex = (currentPage - 1) * entriesPerPage;
              const endIndex = startIndex + entriesPerPage;
              const paginatedEntries = allFilteredEntries.slice(startIndex, endIndex);
              const isOnLastPage = currentPage === totalPages || totalPages === 0;

              return (
                <>
                  {totalEntries > 0 && (
                    <div className="brio-analytics-muted brio-mb-s">
                      Showing {startIndex + 1}-{Math.min(endIndex, totalEntries)} of {totalEntries} loaded entries
                      {useSupabaseDB && hasMore && ' (more available)'}
                    </div>
                  )}

                  {totalEntries === 0 ? (
                    <div className="brio-text-center brio-analytics-muted" style={{ padding: '48px 0' }}>
                      <Clock className="w-12 h-12 mx-auto brio-mb-s" style={{ color: 'rgba(0, 0, 0, 0.2)' }}/>
                      <p>No entries found. Add your first time entry!</p>
                    </div>
                  ) : (
                    <BrioByCode code={PRESENTATION_CODE}>
                      <BrioTable {...tableProps} style={{ maxWidth: '875px', tableLayout: 'fixed' }}>
                        <thead>
                          <tr>
                            <th style={{ width: '44px' }}></th>
                            <th style={{ width: '80px' }}>date</th>
                            <th style={{ width: 'auto' }}>project / client</th>
                            <th style={{ width: 'auto' }}>stage</th>
                            <th className="text-right" style={{ width: '80px' }}>duration</th>
                            <th className="text-center col-actions" style={{ width: '60px' }}>actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedEntries.map(e => {
                            const u = activeUsers.find(x => x.id === e.userId);
                            const p = activeProjects.find(x => x.id === e.projectId);
                            const t = activeTasks.find(x => x.id === e.taskId);
                            const c = activeClients.find(x => x.id === p?.clientId);
                            return (
                              <tr key={e.id} className="brio-row--hoverable">
                                <td>
                                  <div className="brio-flex brio-items-center brio-gap-xs">
                                    <input 
                                      type="checkbox" 
                                      checked={selected.includes(e.id)} 
                                      onChange={(ev) => setSelected(ev.target.checked?[...selected,e.id]:selected.filter(id=>id!==e.id))} 
                                      style={{ width: '16px', height: '16px' }}
                                    />
                                    <BrioColorIndicator color={p?.color} size="compact" />
                                  </div>
                                </td>
                                <td>
                                  <div className="brio-font-medium">{e.date}</div>
                                  {(currentUser.role === 'admin' || currentUser.role === 'admin-pa' || currentUser.role === 'supervisor') && (
                                    <div className="brio-text-xs brio-text-grey brio-italic">{u?.name}</div>
                                  )}
                                </td>
                                <td>
                                  <div className="brio-font-medium">{p?.projectCode || ''}</div>
                                  <div className="brio-text-xs brio-text-grey brio-italic">{c?.organisation || c?.name}</div>
                                </td>
                                <td>
                                  <div className="brio-font-medium">{t?.name}</div>
                                  <div className="brio-text-xs brio-text-grey brio-italic" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.description}</div>
                                </td>
                                <td className="text-right">
                                  <div className="brio-font-medium">{fmtTime(e.duration)}</div>
                                  {(() => {
                                    if (currentUser.role === 'admin' || currentUser.role === 'admin-pa') {
                                      return (
                                        <div className="brio-text-xs" style={{ color: e.billable ? 'var(--brio-gold)' : 'var(--brio-grey)' }}>
                                          {e.billable ? fmtCurr(calcRev(e)) : 'Non-bill'}
                                        </div>
                                      );
                                    }
                                    if (currentUser.role === 'supervisor') {
                                      if (e.userId === currentUser.id) {
                                        return (
                                          <div className="brio-text-xs" style={{ color: e.billable ? 'var(--brio-gold)' : 'var(--brio-grey)' }}>
                                            {e.billable ? fmtCurr(calcRev(e)) : 'Non-bill'}
                                          </div>
                                        );
                                      } else {
                                        return (
                                          <div className="brio-text-xs" style={{ color: e.billable ? 'var(--brio-gold)' : 'var(--brio-grey)' }}>
                                            {e.billable ? 'Billable' : 'Non-billable'}
                                          </div>
                                        );
                                      }
                                    }
                                    return (
                                      <div className="brio-text-xs" style={{ color: e.billable ? 'var(--brio-gold)' : 'var(--brio-grey)' }}>
                                        {e.billable ? 'Billable' : 'Non-billable'}
                                      </div>
                                    );
                                  })()}
                                  {e.approved && <div className="brio-text-xs" style={{ color: 'var(--brio-gold)' }}>✓ Approved</div>}
                                </td>
                                <td className="text-center">
                                  <BrioIconButton
                                    icon="edit"
                                    variant="edit"
                                    title="Edit entry"
                                    onClick={() => {
                                      const h = Math.floor(e.duration/3600);
                                      const m = Math.floor((e.duration%3600)/60);
                                      setEditing({...e, hours: h, minutes: m});
                                    }}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </BrioTable>
                    </BrioByCode>
                  )}

                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="brio-flex brio-items-center brio-justify-between brio-mt-s" style={{ paddingTop: '16px', borderTop: '1px solid var(--brio-border)' }}>
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className={`brio-btn ${currentPage === 1 ? 'brio-btn-secondary' : 'brio-btn-primary'}`}
                        style={currentPage === 1 ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                      >
                        Previous
                      </button>
                      
                      <div className="brio-analytics-muted">
                        Page {currentPage} of {totalPages}
                      </div>
                      
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className={`brio-btn ${currentPage === totalPages ? 'brio-btn-secondary' : 'brio-btn-primary'}`}
                        style={currentPage === totalPages ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                      >
                        Next
                      </button>
                    </div>
                  )}

                  {/* Load More Button - appears when on last page and more entries available */}
                  {useSupabaseDB && hasMore && isOnLastPage && (
                    <div className="brio-flex brio-justify-center brio-mt-s" style={{ paddingTop: '16px' }}>
                      <button
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        className="brio-btn brio-btn-secondary brio-flex brio-items-center brio-gap-xs"
                        style={loadingMore ? { opacity: 0.7, cursor: 'wait' } : {}}
                      >
                        {loadingMore ? (
                          <>
                            <Loader className="w-4 h-4" style={{ animation: 'spin 1s linear infinite' }} />
                            Loading...
                          </>
                        ) : (
                          'Load more entries'
                        )}
                      </button>
                    </div>
                  )}

                  <div className="brio-text-xs brio-text-grey brio-text-center brio-mt-xs">
                    {entriesPerPage} entries per page
                  </div>
                </>
              );
            })()}
          </BrioAnalyticsSurface>
        </div>
      </BrioFrame>
    </div>
  );
}